import { Button } from '@/components/ui/button.tsx';
import {
  PDKConfirmationByPaddockCheckedIcon,
  PDKConfirmationCheckedIcon,
  PDKConfirmationDisabledIcon,
  PDKConfirmationUncheckedIcon,
} from '@/components/ui/pdk-icons.tsx';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/ui/popover.tsx';
import { useUpdateConfirmationMutation } from '@/components/ui/verification/use-update-confirmation-mutation.ts';
import { useHydrationContext } from '@/contexts/hydration/hydration-provider';
import { toast } from '@/hooks/use-toast.ts';
import { TIMESTAMP_FORMAT } from '@/lib/constants.ts';
import { useIsPaddockUser } from '@/lib/get-user-role';
import { cn } from '@/lib/utils.ts';
import { confirmationsStore } from '@/models/stores/confirmations.ts';
import { ConfirmationField, ConfirmationTargetType } from '@/models/types.ts';
import dayjs from 'dayjs';
import { isEmpty as isStringEmpty } from 'lodash';
import { type IObservableValue } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

type ConfirmationDotProps = {
  targetId: string;
  targetType: ConfirmationTargetType;
  field: ConfirmationField;
  value: IObservableValue<string>;
  className?: string;
};

const OPEN_DELAY = 10;
const CLOSE_DELAY = 50;
const LONG_PRESS_MS = 400;

type TimerKind = 'open' | 'close' | 'longPress';
type TimerState = { kind: TimerKind | null; id: number | null };

const OPTIONAL_FIELDS = new Set<ConfirmationField>([
  ConfirmationField.TradeInYMM,
]);

export const CONFIRMATION_POPOVER_ATTR = 'data-confirmation-popover';

const ConfirmationDot = observer(
  ({ targetId, targetType, field, value, className }: ConfirmationDotProps) => {
    const confirmation = confirmationsStore.getByTargetAndField(
      targetId,
      targetType,
      field,
    );
    const mostRecentlyConfirmedBy =
      confirmation?.most_recent_entry?.confirmed_by;
    const { isPaddockUser } = useIsPaddockUser({
      email: mostRecentlyConfirmedBy?.email,
    });

    const [open, setOpen] = React.useState(false);

    const timerRef = React.useRef<TimerState>({ kind: null, id: null });

    const confirmed = !!confirmation?.confirmed;
    const history = confirmation?.history ?? [];
    const { updateConfirmationMutation } = useUpdateConfirmationMutation();
    const { isAPIHydrating } = useHydrationContext();

    const currentValue = value.get();
    const isEmpty = !currentValue.trim().replace(/[,\s]/g, '');
    const isBlockingEmpty = isEmpty && !OPTIONAL_FIELDS.has(field);

    useEffect(() => {
      if (!confirmation) return;

      // DEV NOTE: we don't want to automatically confirm or unconfirm fields during API hydration
      // because we don't know the current state of the field
      if (isAPIHydrating) return;

      const mostRecentValue = confirmation.most_recent_entry?.value;

      // UX note: automatically unconfirms the field if the value is different to the most recent confirmed entry
      // scenario: user changes the primary customer phone number that was confirmed, so it'll automatically unconfirm it
      if (confirmed && mostRecentValue !== currentValue) {
        updateConfirmationMutation.mutate({
          id: uuidv4(),
          targetId,
          targetType,
          field,
          confirmed: false,
          isManuallyToggled: false,
        });
        // UX note: only automatically confirms the field if the value ends up being the same as the most recent confirmed entry
        // AND if it was automatically unconfirmed due to the conditional above.
        // The intention here was to consider if a user accidentally did a misinput in a field, then they amended it by undoing it
      } else if (
        !confirmed &&
        mostRecentValue === currentValue &&
        !confirmation.is_manually_toggled
      ) {
        updateConfirmationMutation.mutate({
          id: uuidv4(),
          targetId,
          targetType,
          field,
          confirmed: true,
          isManuallyToggled: false,
          value: currentValue,
        });
      }
    }, [value.get(), confirmation?.most_recent_entry?.value]);

    const onClick = async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const nextConfirmed = !confirmed;

      if (nextConfirmed && isBlockingEmpty) {
        toast({
          title: 'Cannot confirm empty field',
          description: 'Please enter a value before confirming this field.',
        });

        return;
      }

      await updateConfirmationMutation.mutateAsync({
        id: uuidv4(),
        targetId,
        targetType,
        field,
        confirmed: nextConfirmed,
        isManuallyToggled: true,
        value: nextConfirmed && !isEmpty ? currentValue : '',
      });
    };

    const getIcon = () => {
      if (isBlockingEmpty) {
        return <PDKConfirmationDisabledIcon className="text-gray-300" />;
      }

      if (confirmed) {
        // PRODUCT NOTE: the reason why we need to have a different color for paddock user confirmations
        // is because 1. customers can try to outsmart the system and avoid taxes by giving wrong personal info
        // 2. the paddock user can be wrong
        return isPaddockUser ? (
          <PDKConfirmationByPaddockCheckedIcon />
        ) : (
          <PDKConfirmationCheckedIcon />
        );
      }

      return <PDKConfirmationUncheckedIcon />;
    };

    const canHover = React.useMemo(
      () =>
        typeof window !== 'undefined' && matchMedia('(hover: hover)').matches,
      [],
    );

    const clearTimer = () => {
      const { id } = timerRef.current;
      if (id !== null) window.clearTimeout(id);
      timerRef.current = { kind: null, id: null };
    };

    const setTimer = (kind: TimerKind, ms: number, fn: () => void) => {
      clearTimer();
      const id = window.setTimeout(() => {
        timerRef.current = { kind: null, id: null };
        fn();
      }, ms);
      timerRef.current = { kind, id };
    };

    useEffect(() => clearTimer, []);

    const handleEnter = () => {
      if (!canHover) return;
      setTimer('open', OPEN_DELAY, () => setOpen(true));
    };

    const handleLeave = () => {
      if (!canHover) return;
      setTimer('close', CLOSE_DELAY, () => setOpen(false));
    };

    const onPointerDown = (e: React.PointerEvent) => {
      if (canHover || e.pointerType === 'mouse') return;
      setTimer('longPress', LONG_PRESS_MS, () => setOpen(true));
    };

    const onPointerUpOrCancel = () => {
      if (canHover) return;
      if (timerRef.current.kind === 'longPress') clearTimer();
    };

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <div
          onPointerEnter={handleEnter}
          onPointerLeave={handleLeave}
          onTouchEnd={onPointerUpOrCancel}
        >
          <PopoverAnchor asChild>
            <Button
              size="xxs"
              variant="ghost"
              className={cn(
                className,
                isEmpty && 'hover:bg-transparent dark:hover:bg-transparent',
              )}
              onClick={onClick}
              onPointerDown={onPointerDown}
              onPointerUp={onPointerUpOrCancel}
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-label="Confirmation history"
            >
              {getIcon()}
            </Button>
          </PopoverAnchor>

          <PopoverContent
            side="right"
            align="center"
            className={cn(
              '!w-auto inline-block',
              'whitespace-normal break-words',
              'max-w-[45vw] md:max-w-[500px]',
              'p-3',
            )}
            onPointerEnter={handleEnter}
            onPointerLeave={handleLeave}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            {...{ [CONFIRMATION_POPOVER_ATTR]: '' }}
          >
            <div className="text-xs">
              <div className="mb-2">
                {confirmed ? 'Verified' : 'Verification required'}
              </div>

              {history.length ? (
                <ul className="space-y-1.5">
                  {history.slice(0, 6).map((h) => (
                    <li key={h.id} className="leading-snug">
                      <div className="tabular-nums text-muted-foreground">
                        {dayjs(h.created_at).format(TIMESTAMP_FORMAT)}
                      </div>
                      {!isStringEmpty(h.value) ? (
                        <div className="truncate" title={h.value}>
                          {h.value}
                        </div>
                      ) : (
                        <div className="italic text-muted-foreground">
                          No value
                        </div>
                      )}
                      {h.confirmed_by && (
                        <div className="text-muted-foreground">
                          Confirmed by {h.confirmed_by.username}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="opacity-80">No history yet.</div>
              )}
            </div>
          </PopoverContent>
        </div>
      </Popover>
    );
  },
);

export default ConfirmationDot;
